import { useRef, useState } from 'react';
import { Button, Form, Modal, Space, message, notification } from 'antd';
import AccountReadinessStatus from '../AccountReadinessStatus';
import { analyzeImmediatePublishResponse, buildImmediatePublishParams } from '../contentListPublishFlow';
import { getOperatorScopeId } from '../../utils/operatorScope';

export const useContentListPublish = ({
  navigate,
  user,
  noActivePlan,
  publishEntryBlocked,
  monthlyPublishQuotaExhausted,
  taskQuotaExhausted,
  searchPublishAccounts,
  searchPublishList,
  preparePublishPreflight,
  executePublishNow,
  createPublishTask,
  sortAccountsByReadiness,
  getPublishPreflightMessages,
  resolveErrorMessage,
  setOptimisticPublishingByContentId,
  setDataSource,
  loadData,
}) => {
  const [accountFilterForm] = Form.useForm();
  const [publishTimeForm] = Form.useForm();
  const [publishAccountDrawerVisible, setPublishAccountDrawerVisible] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountDataSource, setAccountDataSource] = useState([]);
  const [accountPagination, setAccountPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [currentPublishRecord, setCurrentPublishRecord] = useState(null);
  const [selectedAccountKeys, setSelectedAccountKeys] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [publishTimeDrawerVisible, setPublishTimeDrawerVisible] = useState(false);
  const [publishType, setPublishType] = useState('immediate');
  const [publishSubmitting, setPublishSubmitting] = useState(false);
  const selectedAccountMapRef = useRef(new Map());
  const publishPollTokenRef = useRef(0);
  const operatorIdRef = useRef(getOperatorScopeId());

  const copyToClipboard = async (text, { successMessage = '已复制' } = {}) => {
    const value = String(text ?? '').trim();
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        message.success(successMessage);
        return;
      }
    } catch (_) {
      // fallback below
    }
    try {
      const el = document.createElement('textarea');
      el.value = value;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      message.success(successMessage);
    } catch (_) {
      message.error('复制失败，请手动复制');
    }
  };

  const openPublishNotification = ({ key, title, description, publishId, contentId, durationSeconds = 10 } = {}) => {
    const actions = (
      <Space size="small">
        <Button
          type="link"
          size="small"
          style={{ padding: '0 4px' }}
          onClick={() => navigate('/content?tab=published')}
        >
          去已发布内容
        </Button>
        {publishId ? (
          <Button
            type="link"
            size="small"
            style={{ padding: '0 4px' }}
            onClick={() => copyToClipboard(publishId, { successMessage: '已复制发布ID' })}
          >
            复制发布ID
          </Button>
        ) : null}
        {contentId ? (
          <Button
            type="link"
            size="small"
            style={{ padding: '0 4px' }}
            onClick={() => copyToClipboard(contentId, { successMessage: '已复制内容ID' })}
          >
            复制内容ID
          </Button>
        ) : null}
      </Space>
    );

    notification.open({
      key,
      message: title || '发布状态',
      description,
      btn: actions,
      duration: durationSeconds,
      placement: 'topRight',
    });
  };

  const renderPreflightIssueContent = (items = []) => {
    const lines = getPublishPreflightMessages({ blockers: items }, 'blockers', 6);
    return (
      <div>
        {lines.map((line) => (
          <div key={line} style={{ marginBottom: 8 }}>{line}</div>
        ))}
      </div>
    );
  };

  const renderPublishExecutionIssueContent = (issues = []) => (
    <div>
      {issues.slice(0, 6).map((issue) => {
        const prefix = [issue.platform, issue.accountName].filter(Boolean).join(' / ');
        return (
          <div key={issue.key} style={{ marginBottom: 8 }}>
            {prefix ? `${prefix}: ` : ''}{issue.message}
          </div>
        );
      })}
    </div>
  );

  const runPublishPreflight = async (params = {}) => {
    const response = await preparePublishPreflight(params);
    if (response?.status !== 'success') {
      throw new Error(resolveErrorMessage(response?.error_message || response?.message, '发布前检查失败'));
    }
    return response;
  };

  const startPollPublishRecord = ({ contentId, notificationKey } = {}) => {
    if (!contentId || !notificationKey) return;
    const token = publishPollTokenRef.current + 1;
    publishPollTokenRef.current = token;

    const maxAttempts = 5;
    const intervalMs = 2000;
    const statusNames = {
      success: '成功',
      failed: '失败',
      pending: '待发布',
      publishing: '发布中',
    };

    const attempt = async (idx) => {
      if (publishPollTokenRef.current !== token) return;
      try {
        const resp = await searchPublishList({
          page: 1,
          page_size: 20,
          filters: { content_id: contentId },
          sort: { field: 'publish_time', order: 'desc' },
        });
        const items = Array.isArray(resp?.items) ? resp.items : [];
        const matched = items.filter((it) => String(it?.content_id || '') === String(contentId));
        if (resp?.status === 'success' && matched.length > 0) {
          const latest = matched[0];
          const status = statusNames[latest?.status] || latest?.status || '-';
          openPublishNotification({
            key: notificationKey,
            title: '已生成发布记录',
            publishId: latest?.publish_id,
            contentId,
            description: (
              <div>
                <div>发布ID：{latest?.publish_id || '-'}</div>
                <div>状态：{status}</div>
              </div>
            ),
            durationSeconds: 10,
          });
          return;
        }
      } catch (_) {
        // ignore: polling should not interrupt user
      }

      if (idx >= maxAttempts) {
        openPublishNotification({
          key: notificationKey,
          title: '已提交立即发布',
          contentId,
          description: '发布处理可能需要更久，请稍后到“已发布内容”查看状态。',
          durationSeconds: 10,
        });
        return;
      }

      setTimeout(() => attempt(idx + 1), intervalMs);
    };

    setTimeout(() => attempt(1), intervalMs);
  };

  const loadPublishAccounts = async (params = {}) => {
    setAccountLoading(true);
    try {
      const page = params.page !== undefined ? params.page : accountPagination.current;
      const page_size = params.page_size !== undefined ? params.page_size : accountPagination.pageSize;

      const filterValues = accountFilterForm.getFieldsValue();
      const filters = {};

      if (filterValues.platforms && filterValues.platforms.length > 0) {
        filters.platforms = Array.isArray(filterValues.platforms) ? filterValues.platforms : [filterValues.platforms];
      }
      if (filterValues.status && filterValues.status.length > 0) {
        filters.status = Array.isArray(filterValues.status) ? filterValues.status : [filterValues.status];
      }

      const requestParams = {
        page,
        page_size,
        operator_id: operatorIdRef.current || undefined,
        keyword: params.keyword || '',
        filters: Object.keys(filters).length > 0 ? filters : (params.filters || {}),
        sort: params.sort || { field: 'created_at', order: 'desc' },
      };

      const data = await searchPublishAccounts(requestParams);
      if (data.status === 'success') {
        setAccountDataSource(sortAccountsByReadiness(data.items || []));
        setAccountPagination({
          current: data.page,
          pageSize: data.page_size,
          total: data.total,
        });
      } else {
        message.error('获取账号列表失败');
      }
    } catch (error) {
      console.error('搜索发布账号失败:', error);
      message.error('搜索发布账号失败，请稍后重试');
    } finally {
      setAccountLoading(false);
    }
  };

  const handleAccountFilter = () => {
    setAccountPagination((prev) => ({
      ...prev,
      current: 1,
    }));
    loadPublishAccounts({ page: 1 });
  };

  const openPublishFlow = (record) => {
    if (publishEntryBlocked) {
      const text = noActivePlan
        ? '当前订阅未生效，请先购买或续费。'
        : '月发布与任务额度均已用完，请前往「付费订阅」升级套餐。';
      message.warning(`${text} 正在前往付费订阅页面...`);
      navigate('/billing');
      return;
    }
    setCurrentPublishRecord(record);
    setPublishAccountDrawerVisible(true);
    setSelectedAccountKeys([]);
    setSelectedAccounts([]);
    selectedAccountMapRef.current.clear();
    accountFilterForm.resetFields();
    loadPublishAccounts();
  };

  const handleConfirmAccountSelection = () => {
    if (selectedAccountKeys.length === 0) {
      message.warning('请至少选择一个发布账号');
      return;
    }
    const selected = selectedAccountKeys
      .map((accountId) => selectedAccountMapRef.current.get(accountId))
      .filter(Boolean);
    setSelectedAccounts(selected);
    setPublishAccountDrawerVisible(false);
    setPublishTimeDrawerVisible(true);
    publishTimeForm.resetFields();
    setPublishType('immediate');
  };

  const handleBackToAccountSelection = () => {
    if (publishSubmitting) return;
    setPublishTimeDrawerVisible(false);
    setPublishAccountDrawerVisible(true);
    publishTimeForm.resetFields();
  };

  const handleConfirmPublish = async ({ skipTaskWarningConfirm = false } = {}) => {
    if (publishSubmitting) return;
    if (noActivePlan) {
      message.warning('当前订阅未生效，请先购买或续费。正在前往付费订阅页面...');
      navigate('/billing');
      return;
    }
    if (publishType === 'immediate' && monthlyPublishQuotaExhausted) {
      message.warning('月发布次数额度已用完，请前往「付费订阅」升级套餐。');
      return;
    }
    if (publishType === 'scheduled' && taskQuotaExhausted) {
      message.warning('任务数量额度已用完，请前往「付费订阅」升级套餐。');
      return;
    }
    if (!currentPublishRecord) {
      message.error('发布内容信息缺失');
      return;
    }
    if (selectedAccounts.length === 0) {
      message.error('未选择发布账号');
      return;
    }

    try {
      const contentIds = [currentPublishRecord.content_id];
      const targetAccountIds = selectedAccounts.map((account) => account.account_id);
      const targetPlatforms = [...new Set(selectedAccounts.map((account) => account.platform))];

      if (publishType === 'immediate') {
        const preflight = await runPublishPreflight({
          operator_id: operatorIdRef.current || undefined,
          intent: 'publish_now',
          content_ids: contentIds,
          target_account_ids: targetAccountIds,
        });
        if (!preflight.ready) {
          Modal.error({
            title: '当前无法立即发布',
            content: renderPreflightIssueContent(preflight.blockers),
          });
          return;
        }

        setPublishSubmitting(true);
        message.loading({ content: '正在提交立即发布请求...', key: 'publish_submit', duration: 0 });
        const params = buildImmediatePublishParams({ currentPublishRecord, targetAccountIds });
        params.operator_id = operatorIdRef.current || undefined;
        const response = await executePublishNow(params);

        if (response.status === 'success') {
          const { issues, allFailed } = analyzeImmediatePublishResponse(response);
          if (allFailed) {
            Modal.error({
              title: '立即发布未成功提交',
              content: renderPublishExecutionIssueContent(issues),
            });
            message.error({
              content: issues[0]?.message || '发布失败',
              key: 'publish_submit',
            });
            return;
          }

          const contentId = currentPublishRecord.content_id;
          const notificationKey = `publish_now_${contentId}_${Date.now()}`;
          setOptimisticPublishingByContentId((prev) => ({
            ...(prev || {}),
            [contentId]: Date.now(),
          }));
          setDataSource((prev) => (prev || []).map((it) => (
            it?.content_id === contentId
              ? { ...it, status: it?.status === '待发布' ? '发布中' : it?.status }
              : it
          )));

          openPublishNotification({
            key: notificationKey,
            title: '已提交立即发布',
            contentId,
            description: (
              <div>
                <div>内容ID：{contentId}</div>
                <div>账号数：{targetAccountIds.length}</div>
                <div>正在等待发布记录生成…</div>
              </div>
            ),
          });
          startPollPublishRecord({ contentId, notificationKey });

          message.success({
            content: (
              <span>
                已提交立即发布，稍后可在“已发布内容”查看状态。
                <Button
                  type="link"
                  size="small"
                  style={{ padding: '0 4px' }}
                  onClick={() => navigate('/content?tab=published')}
                >
                  去查看
                </Button>
              </span>
            ),
            key: 'publish_submit',
            duration: 4,
          });
          if (issues.length > 0) {
            Modal.warning({
              title: '部分账号未成功提交',
              content: renderPublishExecutionIssueContent(issues),
            });
          }

          setPublishTimeDrawerVisible(false);
          setPublishAccountDrawerVisible(false);
          setCurrentPublishRecord(null);
          setSelectedAccounts([]);
          setSelectedAccountKeys([]);
          publishTimeForm.resetFields();
          setTimeout(() => { loadData().catch(() => {}); }, 800);
        } else {
          const structured = resolveErrorMessage(
            response?.error?.error_message || response?.error?.message || response?.error || '',
            ''
          );
          message.error({ content: structured || response?.message || '发布失败', key: 'publish_submit' });
        }
      } else {
        const values = await publishTimeForm.validateFields();
        if (!values.scheduled_time) {
          message.warning('请选择发布时间');
          return;
        }

        const runAt = values.scheduled_time.toISOString();
        const creatorUserId = user?.wechat?.openid || user?.user_id || currentPublishRecord?.creator_user_id || 'aispire';
        const creatorUserName = user?.nickname || user?.wechat?.nickname || user?.name || null;
        const params = {
          task_name: values.task_name,
          task_type: 'cron',
          creator_user_id: creatorUserId,
          creator_user_name: creatorUserName || undefined,
          status: 'active',
          max_items_per_account_per_run: values.max_items_per_account_per_run || 1,
          schedule: {
            trigger_type: 'cron',
            cron_expression: '0 3 * * *',
            run_at: runAt,
            timezone: 'Asia/Shanghai',
          },
          filters: {
            content_types: [currentPublishRecord.content_type || '视频'],
            is_manual_reviewed: currentPublishRecord.is_manual_reviewed !== undefined ? currentPublishRecord.is_manual_reviewed : false,
          },
          target_platforms: targetPlatforms,
          target_account_ids: targetAccountIds,
        };

        const preflight = await runPublishPreflight({
          operator_id: operatorIdRef.current || undefined,
          intent: 'task_create',
          target_platforms: targetPlatforms,
          target_account_ids: targetAccountIds,
        });
        if (!preflight.ready) {
          Modal.error({
            title: '当前无法创建定时任务',
            content: renderPreflightIssueContent(preflight.blockers),
          });
          return;
        }
        if (!skipTaskWarningConfirm && Array.isArray(preflight.warnings) && preflight.warnings.length > 0) {
          Modal.confirm({
            title: '当前任务存在待处理风险，仍要继续创建吗？',
            content: renderPreflightIssueContent(preflight.warnings),
            okText: '仍然创建',
            cancelText: '取消',
            onOk: () => handleConfirmPublish({ skipTaskWarningConfirm: true }),
          });
          return;
        }

        setPublishSubmitting(true);
        const response = await createPublishTask(params);
        if (response.status === 'success') {
          message.success({ content: '定时发布任务已创建', key: 'publish_submit' });
          setPublishTimeDrawerVisible(false);
          setPublishAccountDrawerVisible(false);
          setCurrentPublishRecord(null);
          setSelectedAccounts([]);
          setSelectedAccountKeys([]);
          publishTimeForm.resetFields();
          setPublishType('immediate');
          await loadData();
        } else {
          message.error({ content: response?.message || '创建定时发布任务失败', key: 'publish_submit' });
        }
      }
    } catch (error) {
      console.error('发布失败:', error);
      if (error.errorFields) return;
      const fallback = publishType === 'immediate'
        ? '发布失败，请稍后重试'
        : '创建定时发布任务失败，请稍后重试';
      const content = error?.message || fallback;
      const normalized = resolveErrorMessage(content, fallback);
      const preflightText = content.includes('发布前检查未通过') ? content : normalized;
      message.error({ content: preflightText, key: 'publish_submit' });
    } finally {
      setPublishSubmitting(false);
    }
  };

  const closePublishAccountDrawer = () => {
    setPublishAccountDrawerVisible(false);
    setCurrentPublishRecord(null);
    setAccountDataSource([]);
    setSelectedAccountKeys([]);
    setSelectedAccounts([]);
    selectedAccountMapRef.current.clear();
    accountFilterForm.resetFields();
    setAccountPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
  };

  const closePublishTimeDrawer = () => {
    if (publishSubmitting) return;
    setPublishTimeDrawerVisible(false);
    setPublishType('immediate');
    publishTimeForm.resetFields();
  };

  const handleAccountPageChange = (page, pageSize) => {
    setAccountPagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
    }));
    loadPublishAccounts({ page, page_size: pageSize });
  };

  const handleAccountPageSizeChange = (_, size) => {
    setAccountPagination((prev) => ({
      ...prev,
      current: 1,
      pageSize: size,
    }));
    loadPublishAccounts({ page: 1, page_size: size });
  };

  const handleAccountSelect = (record, selected) => {
    if (!record?.account_id) return;
    if (selected) {
      selectedAccountMapRef.current.set(record.account_id, record);
    } else {
      selectedAccountMapRef.current.delete(record.account_id);
    }
  };

  const handleAccountSelectAll = (selected, _, changeRows) => {
    const rows = Array.isArray(changeRows) ? changeRows : [];
    if (selected) {
      rows.forEach((row) => {
        if (row?.account_id) selectedAccountMapRef.current.set(row.account_id, row);
      });
      return;
    }
    rows.forEach((row) => {
      if (row?.account_id) selectedAccountMapRef.current.delete(row.account_id);
    });
  };

  const handleAccountSelectionChange = (nextSelectedRowKeys) => {
    setSelectedAccountKeys((prevKeys) => {
      const currentPageIds = (accountDataSource || []).map((a) => a.account_id);
      const keepFromOtherPages = (prevKeys || []).filter((key) => !currentPageIds.includes(key));
      return Array.from(new Set([...keepFromOtherPages, ...(nextSelectedRowKeys || [])]));
    });
  };

  const renderAccountReadinessCell = (record) => <AccountReadinessStatus account={record} />;

  return {
    accountFilterForm,
    publishTimeForm,
    publishAccountDrawerVisible,
    accountLoading,
    accountDataSource,
    accountPagination,
    selectedAccountKeys,
    publishTimeDrawerVisible,
    publishSubmitting,
    publishType,
    setPublishType,
    currentPublishRecord,
    selectedAccounts,
    openPublishFlow,
    closePublishAccountDrawer,
    closePublishTimeDrawer,
    handleConfirmAccountSelection,
    handleAccountFilter,
    loadPublishAccounts,
    handleAccountPageChange,
    handleAccountPageSizeChange,
    handleAccountSelect,
    handleAccountSelectAll,
    handleAccountSelectionChange,
    handleBackToAccountSelection,
    handleConfirmPublish,
    renderAccountReadinessCell,
  };
};
